/**
 * Inline input field module
 */

import delegateListener from './delegate-listener';

class InlineInput {
  /**
   * @param {object} [options] -
   * @param {object} [options.selectors] - Configures the selectors used by this module instance
   * @param {function} [options.saveAction] - An optional callback function for when user updates the input value
   */
  constructor(options) {
    const defaults = {
      selectors: {
        container: '.inline-input',
        display: '.inline-input__display',
        input: '.inline-input__input',
        select: '.inline-input .single-select__select',
        save: '.inline-input__save-button',
        edit: '.inline-input__edit-button'
      },
      saveAction: function(source, value) {
        // placeholder function for overrides
        return true;
      }
    };
    this.s = Object.assign({}, defaults, options);
  }

  /**
   * Initializes elements and events
   */
  init() {
    // IE11 doesn't support .forEach for NodeLists, so we use an array instead...
    this.containers = Array.from(
      document.querySelectorAll(this.s.selectors.container)
    );

    // Short circuit initialization if in read only mode
    if(window.READ_ONLY) {
      return;
    }
    this.containers.forEach(this.setEditState(false), this);
    this.boundSaveButtonsDelegateListener = this.saveButtonsDelegateListener.bind(this);
    this.boundEditButtonsDelegateListener = this.editButtonsDelegateListener.bind(this);
    this.boundInputKeydownDelegateListener = this.inputKeydownDelegateListener.bind(this);
    this.boundSelectChangeDelegateListener = this.selectChangeDelegateListener.bind(this);
    document.body.addEventListener('click', delegateListener(
      this.s.selectors.save,
      this.boundSaveButtonsDelegateListener
    ));
    document.body.addEventListener('click', delegateListener(
      this.s.selectors.edit,
      this.boundEditButtonsDelegateListener
    ));
    document.body.addEventListener('change', delegateListener(
      this.s.selectors.select,
      this.boundSelectChangeDelegateListener
    ));
    document.body.addEventListener('keydown', delegateListener(
      this.s.selectors.input,
      this.boundInputKeydownDelegateListener
    ));
  }

  /**
   * Toggles between 'display' and 'edit' states
   *
   * @param {boolean} editing - Truthy values enable edit mode, falsey values enable display mode
   *
   * @returns {function} - Function that applies edit state styles to a given HTMLElement
   */
  setEditState(editing) {
    let editDisplay = editing ? '' : 'none';
    let displayDisplay = editing ? 'none' : '';
    let context = this;
    return function(container) {
      try {
        container.querySelector(context.s.selectors.display).style.display = displayDisplay;
        container.querySelector(context.s.selectors.edit).style.display = displayDisplay;
        container.querySelector(context.s.selectors.input).style.display = editDisplay;
        container.querySelector(context.s.selectors.save).style.display = editDisplay;
      } catch (e) { /* This is fine... */}
    }
  }

  /**
   * Actions for 'save' mode
   *
   * @param {object} event - The event that triggered the action
   * @param {HTMLElement} event.delegateTarget - The event's 'real' source element.
   */
  saveButtonsDelegateListener(event) {
    const container = this.getContainer(event.delegateTarget);
    const input = container.querySelector(this.s.selectors.input);
    if (!this._patternIsValid(input)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const success = this.saveValue(
      input,
      container.querySelector(this.s.selectors.display)
    );
    if(success) this.setEditState(false)(container);
  }

  /**
   * Actions for 'display' mode
   *
   * @param {object} event - The event that triggered the action
   * @param {HTMLElement} event.delegateTarget - The event's 'real' source element.
   */
  editButtonsDelegateListener(event) {
    let container = this.getContainer(event.delegateTarget);
    this.loadValue(
      container.querySelector(this.s.selectors.display),
      container.querySelector(this.s.selectors.input)
    );
    this.setEditState(true)(container);
    let input = container.querySelector(this.s.selectors.input);
    input.focus();
    input.setSelectionRange(0, input.value.length);
  }

  /**
   * An event listener for change events.
   *
   * @param {object} event - The event object passed to the listener
   * @param {HTMLElement} event.delegateTarget - The event's 'real' source element.
   */
  selectChangeDelegateListener(event) {
    if(typeof this.s.saveAction === 'function') {
      this.s.saveAction(event.delegateTarget, event.delegateTarget.value);
    }
  }

  /**
   * An event listener for keydown events.
   *
   * @param {object} event - The event object passed to the listener
   * @param {HTMLElement} event.delegateTarget - The event's 'real' source element.
   */
  inputKeydownDelegateListener(event) {
    let key = event.key.toLowerCase();
    if(
      key !== 'enter' &&
      key !== 'tab' &&
      key !== 'escape' &&
      key !== 'esc'
    ) { return; }
    if (!this._patternIsValid(event.delegateTarget)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    let container = this.getContainer(event.delegateTarget);
    if(key === 'escape' || key === 'esc') {
      this.setEditState(false)(container);
    } else {
      this.saveButtonsDelegateListener(event, container);
    }
  }

  _patternIsValid(element) {
    if(element.pattern) {
      // If pattern validation fails, show validation error to user by triggering form validation:
      return element.checkValidity() || element.form && element.form.reportValidity();
    }
    return true;
  }

  /**
   * Given an element, traverses through it's parents to find something that matches the container selector.
   *
   * @param {HTMLElement} element - The element to start traversing from
   *
   * @returns {(HTMLElement|boolean)} - The container if found, false otherwise
   */
  getContainer(element) {
    let container = element;
    while(!container.matches(this.s.selectors.container)) {
      container = container.parentNode;
      if(container === document.documentElement) return false;
    }
    return container;
  }

  /**
   * Writes a given input's value into a given element's innerText,
   * fires a callback if a callback function was defined in the constructor.
   *
   * @param {HTMLElement} source - The input element with the value that should be written
   * @param {HTMLElement} target - The element to which the value should be written to
   * @returns {Boolean} - Returns false, if field can be validated and is invalid
   */
  saveValue(source, target) {
    target.innerText = source.value;
    if(
      source.validity &&
      ['patternMismatch','valueMissing'].some(key => source.validity[key])
    ) {
      return false;
    }
    if(typeof this.s.saveAction === 'function') {
      return this.s.saveAction(source, source.value);
    }
  }

  /**
   * Reads an element's innerText and populates a given input with it
   *
   * @param {HTMLElement} source - The element that's innerText should be copied
   * @param {HTMLElement} target - The input element to which the innerText should be written into
   */
  loadValue(source, target) {
    target.value = source.innerText;
  }
}

module.exports = InlineInput;
